import { useState } from 'react';
import { getApiErrorMessage } from '@shared/utils/apiError';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Save, X, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { clinicApi } from '../services/clinicApi';
import type { UpdateCapabilitiesRequest } from '../services/clinicApi';
import type { ClinicDetailDto } from '../types/clinic';
import type { ClinicCapabilityType } from '../types/clinic';
import { MaskType } from '../types/clinic';
import RequirePermission from '@shared/components/RequirePermission';
import './ClinicInfoSection.css';

/**
 * Chain-of-custody vendors and the clinic column holding each vendor's platform
 * code. This row (capability key 'electronic_chain_of_custody') is the single home
 * for vendor selection + codes on the profile (SLV2-2999); the standalone eScreen /
 * Formfox capabilities are deactivated in the seed.
 */
const ECOC_VENDORS: ReadonlyArray<{ name: string; codeField: 'eScreenCode' | 'formFoxCode' | 'questCode' }> = [
  { name: 'eScreen', codeField: 'eScreenCode' },
  { name: 'FormFox', codeField: 'formFoxCode' },
  { name: 'Quest', codeField: 'questCode' },
];

interface ClinicCapabilitiesSectionProps {
  clinic: ClinicDetailDto;
  onUpdate: () => void;
}

/**
 * Sub-field form state for capabilities that have additional fields.
 *
 * Clinic Network decision 2026-09-16: the Emergency Room / After Hours / PAT /
 * X-Ray flags and the fit-test type are no longer edited anywhere — the server
 * derives them from the category / capability / procedure lists. Mask Types is
 * the one tile field with no list home, so it lives here as a sub-field of the
 * 'Masks' capability.
 *
 * SLV2-3301 (decision 02, Morgan's one-home rule): DOT certification and its
 * headcount are onboarding data and are edited on the Onboarding card only —
 * the dead 'dot_certification' sub-field is gone (that capability was
 * deactivated). Translation Services is a plain capability (id 10) with no
 * sub-field; the server derives the clinic column from the tag.
 */
interface SubFieldForm {
  maskTypes: string;
  eScreenCode: string;
  formFoxCode: string;
  questCode: string;
  electronicChainOfCustody: string[];
  adapterTypes: string;
}

function buildSubFieldForm(clinic: ClinicDetailDto): SubFieldForm {
  return {
    maskTypes: clinic.maskTypes ?? '',
    eScreenCode: clinic.eScreenCode ?? '',
    formFoxCode: clinic.formFoxCode ?? '',
    questCode: clinic.questCode ?? '',
    electronicChainOfCustody: clinic.electronicChainOfCustody?.split(',').filter(Boolean) ?? [],
    adapterTypes: clinic.adapterTypes ?? '',
  };
}

function formatMaskTypes(maskTypes: string | null | undefined): string | null {
  switch (maskTypes) {
    case 'Half': return 'Half Face';
    case 'Full': return 'Full Face';
    case 'Both': return 'Half & Full Face';
    default: return maskTypes || null;
  }
}

/** Maps capability type key to a view-mode suffix showing its sub-field value */
function getSubFieldDisplay(key: string, clinic: ClinicDetailDto): string | null {
  switch (key) {
    case 'masks':
      return formatMaskTypes(clinic.maskTypes);
    case 'electronic_chain_of_custody': {
      const providers = clinic.electronicChainOfCustody?.split(',').filter(Boolean);
      if (!providers || providers.length === 0) return null;
      // Show the vendor's platform code beside its name when one is stored
      return providers
        .filter(p => p !== 'None')
        .map(p => {
          const codeField = ECOC_VENDORS.find(v => v.name === p)?.codeField;
          const value = codeField ? clinic[codeField] : undefined;
          return value ? `${p}: ${value}` : p;
        })
        .join(', ');
    }
    case 'translation_services':
      return null; // boolean capability, no sub-field display
    case 'adapters':
      return clinic.adapterTypes || null;
    default:
      return null;
  }
}

export default function ClinicCapabilitiesSection({ clinic, onUpdate }: Readonly<ClinicCapabilitiesSectionProps>) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [subFields, setSubFields] = useState<SubFieldForm>(() => buildSubFieldForm(clinic));
  // Snapshot of the onboarding-owned sub-fields as they were when edit mode opened.
  // At save time a field is sent only if it differs from this snapshot; otherwise the
  // freshly fetched value is carried, so a code saved in External Systems while this
  // section sat in edit mode is never overwritten with a stale copy.
  const [editBaseline, setEditBaseline] = useState<SubFieldForm>(() => buildSubFieldForm(clinic));
  const [showSuccess, setShowSuccess] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Sync selected IDs and sub-fields with clinic data when clinic prop changes and not editing
  const [prevClinic, setPrevClinic] = useState(clinic);
  if (clinic !== prevClinic) {
    setPrevClinic(clinic);
    if (!isEditing) {
      setSelectedIds(clinic.capabilityTypeIds ?? []);
      setSubFields(buildSubFieldForm(clinic));
    }
  }

  // Fetch all capability types
  const { data: capabilityTypes = [] } = useQuery({
    queryKey: ['capabilityTypes'],
    queryFn: clinicApi.getCapabilityTypes,
  });

  // Save fans out to three endpoints (capability list, capability fields,
  // onboarding-column fields) — tracked with one saving flag so the
  // Save/Cancel buttons stay disabled for the whole sequence.
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setValidationError(null);
    setIsSaving(true);

    try {
      // 1) Save the capability type checkbox list
      await clinicApi.updateCapabilityList(clinic.id, selectedIds);

      // 2) Save the capability sub-fields (mask types, adapters) via the
      // capabilities endpoint. Its request DTO does not carry the
      // onboarding-column fields — those go through step 3. The five legacy
      // flags (PAT, X-Ray, ER, After Hours, fit-test type) are NOT sent: the
      // server derives them from the list saved in step 1 and the clinic's
      // procedures. A sub-field is cleared when its capability is unticked.
      const masksTypeId = capabilityTypes.find(t => t.key === 'masks')?.id;
      const masksSelected = masksTypeId !== undefined && selectedIds.includes(masksTypeId);
      const adaptersTypeId = capabilityTypes.find(t => t.key === 'adapters')?.id;
      const adaptersSelected = adaptersTypeId !== undefined && selectedIds.includes(adaptersTypeId);
      const request: UpdateCapabilitiesRequest = {
        maskTypes: masksSelected ? subFields.maskTypes || undefined : undefined,
        adapterTypes: adaptersSelected ? subFields.adapterTypes.trim() || undefined : undefined,
      };
      await clinicApi.updateCapabilities(clinic.id, request);

      // 3) Save the sub-fields stored in onboarding columns (vendor codes,
      // ECoC providers, DOT cert, translation services). The onboarding proc
      // overwrites every column it covers, so carry current values for the
      // fields this section doesn't edit — re-fetched at save time (not from
      // the possibly stale `clinic` prop) because the Onboarding and External
      // Systems sections write through the same endpoint (SLV2-3154). Before
      // SLV2-2999 these fields were sent to endpoints that silently dropped
      // them.
      // Electronic Chain of Custody unticked means "no chain-of-custody vendor":
      // clear the vendor list rather than carrying hidden vendors forward.
      const ecocTypeId = capabilityTypes.find(t => t.key === 'electronic_chain_of_custody')?.id;
      const ecocSelected = ecocTypeId !== undefined && selectedIds.includes(ecocTypeId);

      const fresh = await clinicApi.getClinicById(clinic.id);

      // Vendor fields are shared with the External Systems section. Send a field only
      // if it was edited here (differs from the edit-mode snapshot); otherwise carry the
      // fresh value so a save made there while this section was open survives.
      const vendorCodeToSend = (
        field: 'eScreenCode' | 'formFoxCode' | 'questCode',
        freshValue: string | null | undefined,
      ): string | undefined =>
        subFields[field] !== editBaseline[field]
          ? subFields[field].trim() || undefined
          : freshValue ?? undefined;

      const editedList = subFields.electronicChainOfCustody.join(',');
      const editedVendorList =
        editedList !== editBaseline.electronicChainOfCustody.join(',')
          ? editedList || undefined
          : fresh.electronicChainOfCustody ?? undefined;
      await clinicApi.updateClinicOnboarding(clinic.id, {
        preferredMethodOfAppointment: fresh.preferredMethodOfAppointment,
        reportingMethod: fresh.reportingMethod,
        w9OnFile: fresh.w9OnFile,
        // DOT is onboarding data, edited on the Onboarding card; carry the
        // fresh values so this save never overwrites an edit made there.
        dotCertification: fresh.dotCertification,
        numberOfCertifiedDOTProfessionals: fresh.dotCertification ? fresh.numberOfCertifiedDOTProfessionals : undefined,
        electronicChainOfCustody: ecocSelected ? editedVendorList : undefined,
        eScreenCode: vendorCodeToSend('eScreenCode', fresh.eScreenCode),
        formFoxCode: vendorCodeToSend('formFoxCode', fresh.formFoxCode),
        questCode: vendorCodeToSend('questCode', fresh.questCode),
      });

      setIsEditing(false);
      setShowSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['clinic', clinic.id] });
      queryClient.invalidateQueries({ queryKey: ['clinics'] });
      onUpdate();
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      setValidationError(getApiErrorMessage(err, 'Failed to save changes'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedIds(clinic.capabilityTypeIds ?? []);
    setSubFields(buildSubFieldForm(clinic));
    setValidationError(null);
  };

  const handleEdit = () => {
    setSelectedIds(clinic.capabilityTypeIds ?? []);
    const opened = buildSubFieldForm(clinic);
    setSubFields(opened);
    setEditBaseline(opened);
    setValidationError(null);
    setIsEditing(true);
  };

  const toggleCapability = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  const updateSubField = (field: keyof SubFieldForm, value: string | string[]) => {
    setSubFields(prev => ({ ...prev, [field]: value }));
  };

  const toggleECoCProvider = (provider: string) => {
    setSubFields(prev => {
      // 'None' is a valid stored value ("no chain-of-custody vendor") written by the
      // wizard and mutually exclusive with the vendors, so selecting a vendor drops it.
      const current = prev.electronicChainOfCustody.filter(p => p !== 'None');
      if (current.includes(provider)) {
        // The code box is disabled once its vendor is unticked, so the code goes with it.
        const codeField = ECOC_VENDORS.find(v => v.name === provider)?.codeField;
        return {
          ...prev,
          electronicChainOfCustody: current.filter(p => p !== provider),
          ...(codeField ? { [codeField]: '' } : {}),
        };
      }
      return { ...prev, electronicChainOfCustody: [...current, provider] };
    });
  };

  // Build display string for view mode
  const buildViewDisplay = (): string => {
    if (!capabilityTypes.length || !clinic.capabilityTypeIds?.length) return 'None';
    const names = capabilityTypes
      .filter(t => clinic.capabilityTypeIds!.includes(t.id))
      .map(t => {
        const sub = getSubFieldDisplay(t.key, clinic);
        return sub ? `${t.name} (${sub})` : t.name;
      });
    return names.length > 0 ? names.join(', ') : 'None';
  };

  /** Render inline sub-fields for a capability type when editing */
  const renderSubFields = (capType: ClinicCapabilityType) => {
    if (!selectedIds.includes(capType.id)) return null;

    switch (capType.key) {
      case 'masks':
        // Mask Types is the one former wizard-tile field with no list home
        // (Clinic Network decision 2026-09-16), so it hangs off 'Masks'.
        return (
          <div className="info-subfield-container-flex">
            <select
              className="info-select info-subfield-select"
              value={subFields.maskTypes}
              onChange={e => updateSubField('maskTypes', e.target.value)}
              aria-label="Mask Types"
            >
              <option value="">Not Set</option>
              <option value={MaskType.Half}>Half Face</option>
              <option value={MaskType.Full}>Full Face</option>
              <option value={MaskType.Both}>Both</option>
            </select>
          </div>
        );

      case 'adapters':
        return (
          <div className="info-subfield-container">
            <textarea
              className="info-input"
              value={subFields.adapterTypes}
              onChange={e => updateSubField('adapterTypes', e.target.value)}
              placeholder="List the adapter types this clinic stocks (one per line or comma-separated)"
              rows={3}
              aria-label="Adapter types"
            />
          </div>
        );

      case 'electronic_chain_of_custody':
        // Single home for the clinic's chain-of-custody vendors (SLV2-2999): one row per
        // vendor, checkbox on the left and that vendor's platform code beside it. The code
        // box is enabled only while its vendor is checked.
        return (
          <div className="info-vendor-rows">
            {ECOC_VENDORS.map(vendor => {
              const checked = subFields.electronicChainOfCustody.includes(vendor.name);
              return (
                <div key={vendor.name} className="info-vendor-row">
                  <label className="info-checkbox-label info-vendor-row-name">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleECoCProvider(vendor.name)}
                    />
                    <span>{vendor.name}</span>
                  </label>
                  <input
                    type="text"
                    className="info-input info-vendor-row-code"
                    value={subFields[vendor.codeField]}
                    onChange={e => updateSubField(vendor.codeField, e.target.value)}
                    placeholder={`${vendor.name} Code`}
                    aria-label={`${vendor.name} code`}
                    disabled={!checked}
                  />
                </div>
              );
            })}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="info-section">
      {/* Section header with edit/save/cancel */}
      <div className="info-section-header">
        <button
          type="button"
          className="info-section-toggle"
          onClick={() => !isEditing && setIsCollapsed(c => !c)}
          aria-expanded={!isCollapsed}
          disabled={isEditing}
        >
          <ChevronDown size={16} className={`info-section-chevron ${isCollapsed ? 'collapsed' : ''}`} />
          <h3 className="info-section-title">Capabilities</h3>
        </button>
        <div className="info-section-actions">
          {showSuccess && (
            <span className="info-success-toast">
              <CheckCircle size={14} />
              Saved
            </span>
          )}
          {isEditing ? (
            <>
              <button
                type="button"
                className="svc-header-btn svc-header-btn-cancel"
                onClick={handleCancel}
                disabled={isSaving}
              >
                <X size={14} />
                Cancel
              </button>
              <button
                type="button"
                className="svc-header-btn svc-header-btn-save"
                onClick={handleSave}
                disabled={isSaving}
              >
                <Save size={14} />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <RequirePermission permission="UserCanEditClinicCapabilities">
              <button
                type="button"
                className="svc-edit-btn"
                onClick={handleEdit}
                title="Edit capabilities"
              >
                <Pencil size={14} />
                Edit
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      {!isCollapsed && (
        <>
          {validationError && (
            <div className="info-save-error">
              <AlertCircle size={14} />
              {validationError}
            </div>
          )}

          <div className="info-content">
            {isEditing ? (
              <div className="info-details info-details-checkbox-grid">
                {capabilityTypes.map(capType => (
                  <div key={capType.id} className={renderSubFields(capType) ? 'info-checkbox-full-width' : ''}>
                    <label className="info-checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(capType.id)}
                        onChange={() => toggleCapability(capType.id)}
                      />
                      <span>{capType.name}</span>
                    </label>
                    {renderSubFields(capType)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="info-details">
                {/* The PAT / X-Ray / Emergency Room / After Hours / Fit Test rows
                    are gone (Clinic Network decision 2026-09-16): each of those is
                    now read from its single home — the Categories card, the
                    capability list below, or the Services tab. Mask Types shows
                    beside the 'Masks' capability. */}
                <div className="info-row">
                  <div className="info-field info-field-wide">
                    <label>Assigned Capabilities</label>
                    <span>{buildViewDisplay()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
